/**
 * Netlist extraction.
 *
 * CIRCAD does not keep a separate connectivity database. A net is whatever the
 * geometry says it is: primitives carry a `Signal:` name, and netscan walks the
 * geometry to find what touches what (OGnet0.cpp). Connectivity and DRC are
 * therefore the same traversal, and a net name is an *assertion* that the scan
 * either confirms or flags as a conflict.
 *
 * This module reproduces that. It handles both document kinds:
 *
 *   schematic — wires on Schematic-type layers, pins (T0 pads) inside
 *               components, junction dots (S3), and net-label text
 *   pcb       — traces and arcs on Copper/Plane layers, pads and vias, with
 *               through-hole pads bridging every copper layer
 */

import {
  type CadDocument, type ComponentEntity, type Entity, type FillEntity,
  type LineEntity,
  type PadEntity, type Vertex, LayerType,
  isElectrical, padIsThrough, padIsSchematic,
} from '../model.ts';
import { padThrough } from '../geom/pad.ts';

/** Coincidence tolerance in inches. Tighter than any real grid, looser than fp noise. */
const EPS = 1e-6;

export interface PinRef {
  /** Reference designator, resolved from the component's P1 parameter. */
  ref: string;
  /** Component name, e.g. `7404`. */
  component: string;
  componentId: number;
  /** Pin name/number from the pad. */
  pin: string;
  x: number;
  y: number;
  /**
   * Which sheet of the design this pin was found on, from zero.
   *
   * Set only by {@link extractDesignNetlist}, which is the only caller that has
   * more than one sheet to tell apart. A single-sheet scan leaves it alone —
   * there is nothing to say — so absent means "the sheet you are looking at".
   */
  sheet?: number;
  /** That sheet's name, for saying so without a lookup. */
  sheetName?: string;
}

export interface Net {
  /** Resolved name, or a generated `N$nnn` when the net is unnamed. */
  name: string;
  /** True when the name came from the file rather than being generated. */
  named: boolean;
  pins: PinRef[];
  /**
   * Ids of every primitive belonging to this net.
   *
   * Meaningful against **one** document. A design netlist unions these across
   * sheets, where ids are only unique within a sheet, so the editor's own
   * highlighting and net-naming go on scanning the sheet in front of them
   * rather than reading this.
   */
  entityIds: number[];
  /**
   * Sheets this net appears on, from zero — set only by a design netlist.
   *
   * More than one means a port did its job and carried the name across.
   */
  sheets?: number[];
  /**
   * Pins grouped by what copper actually joins them.
   *
   * A net names what *should* be connected; each group is what *is* connected.
   * One group means the net is fully routed. Two or more means the gaps
   * between them are still open — which is exactly what the ratsnest draws, so
   * routing a trace merges two groups and its ratline disappears on its own.
   */
  groups: PinRef[][];
}

export interface NetConflict {
  kind: 'signal-mismatch' | 'unnamed-pad' | 'multiple-labels';
  message: string;
  entityIds: number[];
}

export interface Netlist {
  nets: Net[];
  conflicts: NetConflict[];
  /** Pins that belong to no net and were meant to — nothing touches them. */
  unconnected: PinRef[];
  /**
   * Pins that belong to no net and are meant to: the sheet marks them idle.
   *
   * Kept apart from {@link unconnected} because the two want opposite
   * treatment. On the sample Altium sheets 90 of 98 loose pins are marked, so
   * counting them together says a quarter of the STM32's pins are missing when
   * three are.
   */
  noConnect: PinRef[];
}

// ---------------------------------------------------------------------------
// Union-Find
// ---------------------------------------------------------------------------

class DisjointSet {
  private parent: number[] = [];

  make(): number {
    this.parent.push(this.parent.length);
    return this.parent.length - 1;
  }

  find(a: number): number {
    let root = a;
    while (this.parent[root] !== root) root = this.parent[root]!;
    // Path compression.
    let cur = a;
    while (this.parent[cur] !== root) {
      const next = this.parent[cur]!;
      this.parent[cur] = root;
      cur = next;
    }
    return root;
  }

  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
}

// ---------------------------------------------------------------------------
// Spatial index
// ---------------------------------------------------------------------------

/**
 * Point bucket keyed on a quantised grid. Nets are found by coincidence, so a
 * hash on rounded coordinates turns an O(n²) scan into near-linear.
 */
class PointIndex {
  private buckets = new Map<string, number[]>();
  /** Bucket size — must exceed EPS by enough that neighbours are reachable. */
  private readonly cell = 0.001;

  private key(x: number, y: number): string {
    return `${Math.round(x / this.cell)}:${Math.round(y / this.cell)}`;
  }

  add(x: number, y: number, node: number): void {
    // Insert into the 3×3 neighbourhood so lookups need only probe one cell.
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const k = this.key(x + dx * this.cell, y + dy * this.cell);
        let b = this.buckets.get(k);
        if (!b) { b = []; this.buckets.set(k, b); }
        if (!b.includes(node)) b.push(node);
      }
    }
  }

  near(x: number, y: number): number[] {
    return this.buckets.get(this.key(x, y)) ?? [];
  }
}

// ---------------------------------------------------------------------------
// Flattening
// ---------------------------------------------------------------------------

/** A primitive lifted into document coordinates, with its owning component. */
/**
 * The widest any pad is assumed to be, for the search that skips pads a trace
 * cannot reach. Half an inch is larger than any pad on a board; too large only
 * costs a few extra comparisons, too small would miss a join.
 */
const MAX_PAD_SPAN = 0.5;

interface FlatEntity {
  entity: Entity;
  owner: ComponentEntity | null;
  /** Connection points in document space. */
  points: Vertex[];
  layer: number;
}

export function rotatePoint(x: number, y: number, deg: number): Vertex {
  if (!deg) return { x, y };
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return { x: x * c - y * s, y: x * s + y * c };
}

/**
 * Transform a component-local point into document space, applying mirror then
 * rotation then translation — the order the original composes them.
 */
export function placeInComponent(p: Vertex, comp: ComponentEntity): Vertex {
  const mx = comp.mirrored ? -p.x : p.x;
  const r = rotatePoint(mx, p.y, comp.rotation ?? 0);
  return { x: r.x + comp.x, y: r.y + comp.y };
}

/** Collect the connection points of a primitive, in document space. */
function connectionPoints(e: Entity, comp: ComponentEntity | null): Vertex[] {
  const local: Vertex[] = [];
  switch (e.kind) {
    case 'line':
      local.push({ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 });
      break;
    case 'arc': {
      // Arcs connect at their endpoints.
      const a1 = (e.angle * Math.PI) / 180;
      const a2 = ((e.angle + e.sweep) * Math.PI) / 180;
      local.push(
        { x: e.cx + e.radius * Math.cos(a1), y: e.cy + e.radius * Math.sin(a1) },
        { x: e.cx + e.radius * Math.cos(a2), y: e.cy + e.radius * Math.sin(a2) },
      );
      break;
    }
    case 'pad':
      local.push({ x: e.x, y: e.y });
      break;
    case 'fill':
      for (const v of e.vertices) local.push(v);
      break;
    default:
      return [];
  }
  return comp ? local.map((p) => placeInComponent(p, comp)) : local;
}

/** Whether a point lies on the segment through `ends`. */
function onSegment(p: Vertex, ends: Vertex[]): boolean {
  const a = ends[0];
  const b = ends[1];
  if (!a || !b) return false;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = dx * dx + dy * dy;
  if (len === 0) return Math.hypot(p.x - a.x, p.y - a.y) <= EPS;
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)) <= EPS;
}

function flatten(doc: CadDocument): FlatEntity[] {
  const out: FlatEntity[] = [];
  const visit = (list: Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { visit(e.children, e); continue; }
      out.push({ entity: e, owner, points: connectionPoints(e, owner), layer: e.layer });
    }
  };
  visit(doc.entities, null);
  return out;
}

/** Reference designator for a component, from its P1 parameter. */
export function refDesOf(c: ComponentEntity): string {
  return (c.params[1] ?? '').trim() || c.name;
}

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

export function extractNetlist(doc: CadDocument): Netlist {
  const flat = flatten(doc);
  const ds = new DisjointSet();
  const index = new PointIndex();
  const conflicts: NetConflict[] = [];

  // One set member per primitive.
  const nodeOf = new Map<number, number>();
  for (const f of flat) nodeOf.set(f.entity.id, ds.make());

  /**
   * Layers that carry connectivity.
   *
   * In a schematic only the Schematic-type (signal/bus) layers conduct — the
   * Symbols layer holds gate outlines and bubbles, and treating those as wires
   * would short every pin of a part together.
   */
  const conductive = (layer: number): boolean => {
    const l = doc.layers[layer];
    if (!l) return false;
    return doc.kind === 'sch' ? l.type === LayerType.Schematic : isElectrical(l.type);
  };

  /** True when two primitives share a layer, or one bridges layers. */
  const layersConnect = (a: FlatEntity, b: FlatEntity): boolean => {
    if (doc.kind === 'sch') return true;
    const bridging = (f: FlatEntity): boolean =>
      f.entity.kind === 'pad' && padIsThrough((f.entity as PadEntity).type);
    if (bridging(a) || bridging(b)) return true;
    return a.layer === b.layer;
  };

  // --- Pass 1: index every connection point of every conductive primitive.
  const active = flat.filter((f) => {
    if (f.entity.kind === 'pad') {
      const p = f.entity as PadEntity;
      // Schematic pins are conductive in schematics; in a PCB they are not copper.
      if (doc.kind === 'sch') return true;
      return !padIsSchematic(p.type);
    }
    if (f.entity.kind === 'line' || f.entity.kind === 'arc') return conductive(f.layer);
    return false;
  });

  for (const f of active) {
    const node = nodeOf.get(f.entity.id)!;
    for (const p of f.points) index.add(p.x, p.y, node);
  }

  // --- Pass 2: union primitives that share a point.
  const byNode = new Map<number, FlatEntity>();
  for (const f of active) byNode.set(nodeOf.get(f.entity.id)!, f);

  // On a schematic, wires that merely cross or branch are NOT connected unless
  // an explicit junction dot (or a pin) sits there — the original's rule, and
  // why it has a Place Signal Dot command. So count wire-ends per point and note
  // the junctions; a point where 3+ wire-ends meet with no dot/pin is a crossing
  // of separate nets, not a node. A plain 2-way corner still joins freely. On a
  // board, copper touching copper always connects, so this does not apply.
  const cell = 0.001;
  const pkey = (x: number, y: number): string =>
    `${Math.round(x / cell)}:${Math.round(y / cell)}`;
  const wireEnds = new Map<string, number>();
  const junction = new Set<string>();
  if (doc.kind === 'sch') {
    for (const f of active) {
      if (f.entity.kind === 'line') {
        for (const p of f.points) wireEnds.set(pkey(p.x, p.y), (wireEnds.get(pkey(p.x, p.y)) ?? 0) + 1);
      } else if (f.entity.kind === 'pad') {
        const p = f.points[0];
        if (p) junction.add(pkey(p.x, p.y)); // a pin or junction dot makes a node
      }
    }
  }

  for (const f of active) {
    const node = nodeOf.get(f.entity.id)!;
    for (const p of f.points) {
      for (const other of index.near(p.x, p.y)) {
        if (other === node) continue;
        const of = byNode.get(other);
        if (!of || !layersConnect(f, of)) continue;
        if (!touches(f, of, p)) continue;
        if (doc.kind === 'sch' && f.entity.kind === 'line' && of.entity.kind === 'line') {
          const k = pkey(p.x, p.y);
          if ((wireEnds.get(k) ?? 0) > 2 && !junction.has(k)) continue; // crossing, not a node
        }
        ds.union(node, other);
      }
    }
  }

  // --- Pass 2b: copper that overlaps is one conductor.
  //
  // On a board two tracks are the same copper when their strokes overlap; they
  // do not have to meet end to end. Requiring an exact meeting read solid
  // copper as an open, because a file need not agree with itself to the
  // millionth of an inch: on the ESP32 board a route ran from J1-12 through a
  // via to U1-13 and still drew a ratline across it, because two of its
  // bottom-layer segments turned a corner 0.00064" apart.
  //
  // The test is physical — within the two half-widths — so it closes a rounded
  // corner without joining tracks that merely pass near each other. Schematics
  // are untouched: a wire there connects at its ends, under the junction rules
  // above, and a sheet has no copper to overlap.
  if (doc.kind === 'pcb') {
    const traces = active.filter(
      (f) => (f.entity.kind === 'line' || f.entity.kind === 'arc') && f.points.length >= 2);

    const halfOf = (f: FlatEntity): number =>
      (('width' in f.entity ? (f.entity as LineEntity).width : 0) || 0) / 2;

    let widest = 0;
    const spans = traces.map((f) => {
      const h = halfOf(f);
      if (h > widest) widest = h;
      const a = f.points[0]!;
      const b = f.points[f.points.length - 1]!;
      return {
        f, h,
        x1: Math.min(a.x, b.x) - h, y1: Math.min(a.y, b.y) - h,
        x2: Math.max(a.x, b.x) + h, y2: Math.max(a.y, b.y) + h,
      };
    }).sort((p, q) => p.x1 - q.x1);
    const spanStarts = spans.map((s) => s.x1);
    const reach = widest * 2;

    for (let i = 0; i < spans.length; i++) {
      const a = spans[i]!;
      const an = nodeOf.get(a.f.entity.id)!;
      // Only boxes that can still begin before this one ends are worth reading.
      let lo = 0;
      let hi = spanStarts.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (spanStarts[mid]! < a.x1 - reach) lo = mid + 1; else hi = mid;
      }
      for (let j = lo; j < spans.length; j++) {
        if (j === i) continue;
        const b = spans[j]!;
        if (b.x1 > a.x2) break;
        if (b.x2 < a.x1 || b.y1 > a.y2 || b.y2 < a.y1) continue;
        if (!layersConnect(a.f, b.f)) continue;
        const bn = nodeOf.get(b.f.entity.id)!;
        if (ds.find(an) === ds.find(bn)) continue;
        if (strokesMeet(a.f, b.f, a.h + b.h)) ds.union(an, bn);
      }
    }
  }

  // --- Pass 3: a trace touching a pad connects to it.
  //
  // Touching, not ending in. A track that runs straight through a pad on its
  // way somewhere else is the same copper as the pad, and daisy-chaining pads
  // that way is ordinary practice — but only the endpoints used to be tested,
  // so those pads read as unconnected and the net came back reported as an
  // open that is not there.
  const pads = active.filter((f) => f.entity.kind === 'pad');

  // Every pad boxed once, and sorted by where it starts, so a trace can skip
  // straight to the pads that could possibly touch it.
  //
  // Testing every trace against every pad is a thousand pads times ten
  // thousand tracks, and once the test grew from "does an end land here" to
  // "does any part of this cross here" that went from slow to unusable — six
  // seconds on a board of five thousand objects. The boxes settle almost every
  // pair for the price of four comparisons.
  const boxes = pads.map((pf) => {
    const pad = pf.entity as PadEntity;
    const c = pf.points[0] ?? { x: 0, y: 0 };
    // The widest the pad can be however it is turned.
    const r = Math.hypot(pad.sizeX, pad.sizeY) / 2 + EPS;
    return { pf, x1: c.x - r, y1: c.y - r, x2: c.x + r, y2: c.y + r };
  }).sort((a, b) => a.x1 - b.x1);
  const starts = boxes.map((b) => b.x1);

  for (const f of active) {
    if (f.entity.kind === 'pad') continue;
    if (f.points.length === 0) continue;
    const traceLike = f.entity.kind === 'line' || f.entity.kind === 'arc';
    const node = nodeOf.get(f.entity.id)!;

    let tx1 = Infinity; let ty1 = Infinity; let tx2 = -Infinity; let ty2 = -Infinity;
    for (const p of f.points) {
      tx1 = Math.min(tx1, p.x); tx2 = Math.max(tx2, p.x);
      ty1 = Math.min(ty1, p.y); ty2 = Math.max(ty2, p.y);
    }

    // First pad whose box could reach this trace: binary search on the sorted
    // starts, then walk while the boxes can still begin before the trace ends.
    let lo = 0;
    let hi = starts.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid]! < tx1 - MAX_PAD_SPAN) lo = mid + 1; else hi = mid;
    }

    for (let i = lo; i < boxes.length; i++) {
      const b = boxes[i]!;
      if (b.x1 > tx2) break;                       // and every one after it
      if (b.x2 < tx1 || b.y1 > ty2 || b.y2 < ty1) continue;
      if (!layersConnect(f, b.pf)) continue;
      const other = nodeOf.get(b.pf.entity.id)!;
      if (ds.find(node) === ds.find(other)) continue;   // already the same copper

      let hit = false;
      for (let k = 0; k < f.points.length && !hit; k++) {
        if (pointInPad(f.points[k]!, b.pf)) hit = true;
      }
      // Then the stretches between them, which is where a pad passed over
      // rather than stopped at will be found.
      //
      // Tracks only. A pour's outline is thousands of vertices and testing
      // every one of its edges against every pad it spans took thirty-seven
      // seconds on the largest board here — and answers a question already
      // settled below, where a pour is joined to the pads on its own net that
      // it covers. A hand-drawn solid keeps the vertex test it always had.
      if (!hit && traceLike) {
        for (let k = 0; k + 1 < f.points.length && !hit; k++) {
          if (segmentInPad(f.points[k]!, f.points[k + 1]!, b.pf)) hit = true;
        }
      }
      if (hit) ds.union(node, other);
    }
  }

  // --- Pass 3b: a flood pour joins the pads on its net that it covers.
  //
  // The pour is copper on its net, cleared around everything else, so the pads
  // it does not clear — those on its own signal, that reach its layer, whose
  // centre falls inside its region — are all one piece of copper. Union them so
  // they collapse into a single island and their ratlines retire, the same way
  // routing a trace between them would.
  for (const ff of flat) {
    if (ff.entity.kind !== 'fill') continue;
    const fill = ff.entity as FillEntity;
    const net = fill.signal;
    if (!fill.flood || !net) continue;
    const region = fill.vertices.length >= 3 ? fill.vertices : rectToPoly(fill.bounds);

    let anchor: number | null = null;
    for (const pf of pads) {
      const pad = pf.entity as PadEntity;
      if (pad.signal !== net) continue;
      if (!(padThrough(pad) || pf.layer === ff.layer)) continue; // reaches this layer?
      const c = pf.points[0];
      if (!c || !pointInPolygon(c, region)) continue;
      const node = nodeOf.get(pf.entity.id)!;
      if (anchor === null) anchor = node;
      else ds.union(anchor, node);
    }
  }

  // --- Pass 3c: imported planes and pours. An imported board does not carry our
  // flood FillEntity; its plane copper is declared by the file's FloodFill
  // record (a hot signal poured on a target layer) and by Plane-type layers.
  // Either way the plane is solid copper of one net, so every pad on that net
  // that reaches the layer is tied to it — which is what makes a ground plane's
  // pads read as connected rather than a field of ratlines.
  const tiePlane = (net: string, layer: number): void => {
    if (!net) return;
    let anchor: number | null = null;
    for (const pf of pads) {
      const pad = pf.entity as PadEntity;
      if (pad.signal !== net) continue;
      if (!(padThrough(pad) || pf.layer === layer)) continue;
      const node = nodeOf.get(pf.entity.id)!;
      if (anchor === null) anchor = node;
      else ds.union(anchor, node);
    }
  };
  /**
   * Is there actually a plane there?
   *
   * The FloodFill record is a *setting* — which net to pour and where — not a
   * statement that the pour was ever made. Taking it as one ties every pad on
   * the hot signal together whether or not any copper joins them, so a board
   * whose ground plane was never poured, or has been deleted, reports its
   * ground as fully wired. On the board that raised this, 182 GND pads with
   * not one track between them came back as a single connected group while
   * VCC and 5VOLTS — in exactly the same state — correctly showed as open.
   *
   * A Plane-type layer is different and is taken at its word: it is drawn in
   * negative, so its copper is everything that has *not* been cleared and
   * there is nothing positive to go looking for.
   */
  /** Anything drawn on a layer — how a negative plane says it is a real plane. */
  const countOn = (layer: number): number => {
    let n = 0;
    for (const f of flat) if (f.layer === layer) n++;
    return n;
  };

  const planeHasCopper = (net: string, layer: number): boolean => {
    if (doc.layers[layer]?.type === LayerType.Plane) return true;
    for (const f of active) {
      if (f.entity.kind === 'pad') continue;
      if (f.layer !== layer) continue;
      if ((f.entity as { signal?: string }).signal === net) return true;
    }
    return false;
  };

  if (doc.kind === 'pcb') {
    const hot = doc.floodFill?.hotSignal;
    const target = doc.floodFill?.targetLayer ?? 0;
    if (hot && planeHasCopper(hot, target)) tiePlane(hot, target);
    doc.layers.forEach((l, i) => {
      if (l.type !== LayerType.Plane) return;
      // A plane layer that nobody has assigned a net to and that has nothing on
      // it is a name in the stack, not a plane. Reading its *name* as its net
      // tied every GND pad on a stock board together the moment the netlist ran
      // — ground came back fully wired on a board with no ground copper at all,
      // and no ratline ever appeared to say otherwise. `planeLayers` in
      // route/netclass.ts has always drawn the line here; this now agrees with
      // it, so what the ratsnest shows and what the router routes are the same
      // board. Net List In assigns the net when the sheet asks for it.
      if (l.net) tiePlane(l.net.trim(), i);
      else if (countOn(i) > 0) tiePlane(l.name.trim(), i);
    });
  }

  // --- Pass 3b: a net label names the wire it sits on.
  //
  // Lettering, not copper: it joins nothing, it only says what is under it.
  // Most sit mid-span rather than at a wire end — 79 of 108 on one Altium
  // sheet — so the point index, which holds connection points only, cannot
  // find them; the anchor is tested against the run of each wire instead.
  //
  // Without this an imported sheet comes out almost entirely unnamed: on the
  // STM32 sample only 4 of 21 nets had a name, all from power ports, and the
  // other 17 were invented as `N$nnn`. Pushing that onto its own board renamed
  // the copper and every net whose real name differed read as a short.
  const labelled = new Map<number, Set<string>>();
  if (doc.kind === 'sch') {
    const wires = active.filter((f) => f.entity.kind === 'line');
    for (const f of flat) {
      if (f.entity.kind !== 'text' || !conductive(f.layer)) continue;
      const text = f.entity.text.trim();
      if (!text) continue;
      const at = f.owner
        ? placeInComponent({ x: f.entity.x, y: f.entity.y }, f.owner)
        : { x: f.entity.x, y: f.entity.y };
      const on = wires.find((w) => onSegment(at, w.points));
      if (!on) continue;
      const root = ds.find(nodeOf.get(on.entity.id)!);
      let names = labelled.get(root);
      if (!names) { names = new Set(); labelled.set(root, names); }
      names.add(text);
    }
  }

  // --- Pass 4: group into nets and resolve names.
  const groups = new Map<number, FlatEntity[]>();
  for (const f of active) {
    const root = ds.find(nodeOf.get(f.entity.id)!);
    let g = groups.get(root);
    if (!g) { g = []; groups.set(root, g); }
    g.push(f);
  }

  const nets: Net[] = [];
  const unconnected: PinRef[] = [];
  const noConnect: PinRef[] = [];
  let generated = 0;

  for (const [root, group] of groups) {
    const names = new Set<string>();
    for (const f of group) {
      const sig = (f.entity as { signal?: string }).signal;
      if (sig) names.add(sig);
    }
    // A label on any wire of the group names the whole group.
    for (const n of labelled.get(root) ?? []) names.add(n);

    // `$nnnn` names are CIRCAD's placeholders for *unnamed* nets, not real
    // labels. A group carrying a mix of them and one real name is simply that
    // real net with some unnamed segments — not a conflict. Only two or more
    // *real* names colliding is a genuine wrong-signal-name error.
    const real = [...names].filter((n) => !/^\$\d+$/.test(n));
    let name: string;
    let named = true;
    if (names.size === 0) {
      name = `N$${String(++generated).padStart(3, '0')}`;
      named = false;
    } else if (real.length <= 1) {
      name = real[0] ?? [...names][0]!;
      named = real.length === 1;
    } else {
      // Two or more real names on one net: the original reports a wrong-signal
      // error rather than silently merging; so do we.
      const sorted = real.sort();
      name = sorted[0]!;
      conflicts.push({
        kind: 'signal-mismatch',
        message: `connected geometry carries conflicting signal names: ${sorted.join(', ')}`,
        entityIds: group.map((f) => f.entity.id),
      });
    }

    const pins: PinRef[] = [];
    for (const f of group) {
      if (f.entity.kind !== 'pad' || !f.owner) continue;
      const pad = f.entity as PadEntity;
      if (!pad.pin) continue;
      const pt = f.points[0] ?? { x: pad.x, y: pad.y };
      pins.push({
        ref: refDesOf(f.owner),
        component: f.owner.name,
        componentId: f.owner.id,
        pin: pad.pin,
        x: pt.x,
        y: pt.y,
      });
    }

    // A group of one pad and nothing else is a loose pin — reported as missed
    // only if the sheet has not already said it means it.
    if (pins.length === 1 && group.length === 1) {
      const only = group[0]!.entity;
      const marked = only.kind === 'pad' && (only as PadEntity).noConnect === true;
      (marked ? noConnect : unconnected).push(pins[0]!);
      if (!named) continue;
    }

    nets.push({
      name,
      named,
      pins,
      entityIds: group.map((f) => f.entity.id),
      // One geometric group at this stage; merging by name collects the rest.
      // A *copy*, not the same array: merging pushes into `pins`, and sharing
      // the reference made every island silently accumulate the others' pins,
      // so the ratsnest then picked a pin against itself and drew a
      // zero-length line.
      groups: pins.length > 0 ? [[...pins]] : [],
    });
  }

  // Merge groups that share a signal name.
  //
  // Geometry alone cannot see these: on a freshly transferred board the pads
  // of a net carry the same `Signal:` but no copper joins them yet, so they
  // arrive here as separate groups. In CIRCAD a net *is* every primitive with
  // that name, so they are one net — and without this the ratsnest, whose
  // whole job is to show what shares a name but is not yet routed, would have
  // nothing to draw.
  const merged = new Map<string, Net>();
  // An unnamed net is one island of copper that nothing has claimed, so it must
  // never merge — not with another unnamed island, and not with a named net
  // that happens to carry the same generated name. Keeping them out of the map
  // says that plainly; they used to be given a sentinel key instead.
  const loose: Net[] = [];
  for (const net of nets) {
    if (!net.named) {
      loose.push(net);
      continue;
    }
    const existing = merged.get(net.name);
    if (!existing) {
      merged.set(net.name, net);
      continue;
    }
    existing.pins.push(...net.pins);
    existing.entityIds.push(...net.entityIds);
    // Each merged net was one island of copper; keep them apart so the
    // ratsnest can see what still needs joining.
    existing.groups.push(...net.groups);
  }

  const out = [...merged.values(), ...loose];
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  return { nets: out, conflicts, unconnected, noConnect };
}

// ---------------------------------------------------------------------------
// Geometry predicates
// ---------------------------------------------------------------------------

/**
 * True when two traces' strokes meet — an end of one lying on the other.
 *
 * Ends only, deliberately. It is an end that a file rounds inconsistently, and
 * testing whole strokes against each other would fuse two tracks running side
 * by side on a tight pitch. Two tracks that cross in their middles are a short,
 * not a connection, and the clearance scan is what reports those.
 */
function strokesMeet(a: FlatEntity, b: FlatEntity, tol: number): boolean {
  const ends = (f: FlatEntity): [Vertex, Vertex] =>
    [f.points[0]!, f.points[f.points.length - 1]!];
  const [a1, a2] = ends(a);
  const [b1, b2] = ends(b);
  return pointNearSegment(a1, b1, b2, tol) || pointNearSegment(a2, b1, b2, tol)
    || pointNearSegment(b1, a1, a2, tol) || pointNearSegment(b2, a1, a2, tol);
}

/** Distance from a point to a segment, against a tolerance. */
function pointNearSegment(p: Vertex, a: Vertex, b: Vertex, tol: number): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = dx * dx + dy * dy;
  let t = len === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / len;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)) <= tol;
}

/** True when `b` has a connection point coincident with `p`. */
function touches(a: FlatEntity, b: FlatEntity, p: Vertex): boolean {
  void a;
  for (const q of b.points) {
    if (Math.abs(q.x - p.x) <= EPS && Math.abs(q.y - p.y) <= EPS) return true;
  }
  return false;
}

/** Four corners of a bounding box, as a polygon. */
function rectToPoly(b: { x1: number; y1: number; x2: number; y2: number }): Vertex[] {
  return [
    { x: b.x1, y: b.y1 }, { x: b.x2, y: b.y1 },
    { x: b.x2, y: b.y2 }, { x: b.x1, y: b.y2 },
  ];
}

/** Ray-casting point-in-polygon test. */
function pointInPolygon(p: Vertex, poly: Vertex[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    const straddles = (a.y > p.y) !== (b.y > p.y);
    if (straddles && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/** True when a point lies within a pad's copper extent. */
function pointInPad(p: Vertex, pf: FlatEntity): boolean {
  const pad = pf.entity as PadEntity;
  const c = pf.points[0];
  if (!c) return false;

  // Undo the pad's own rotation so the test is axis-aligned.
  const rot = -(pad.rotation ?? 0) - (pf.owner?.rotation ?? 0);
  const d = rotatePoint(p.x - c.x, p.y - c.y, rot);

  const hx = pad.sizeX / 2;
  const hy = pad.sizeY / 2;
  if (hx <= 0 || hy <= 0) {
    return Math.abs(d.x) <= EPS && Math.abs(d.y) <= EPS;
  }
  if (pad.type.charAt(1) === '2') {
    // Rectangular.
    return Math.abs(d.x) <= hx + EPS && Math.abs(d.y) <= hy + EPS;
  }
  // Round or oval.
  return (d.x / hx) ** 2 + (d.y / hy) ** 2 <= 1 + EPS;
}

/**
 * Does a stretch of track cross a pad, rather than merely end in one?
 *
 * A trace that runs *through* a pad is the same piece of copper as the pad —
 * it does not have to stop there to be joined to it. Only the endpoints were
 * ever tested, so a pad daisy-chained by a track passing over it read as
 * unconnected, and the net came back split. On the board this was found on,
 * that is a track running from one pad straight through a second to a third:
 * copper anyone would call connected, reported as an open.
 *
 * The test is done in the pad's own frame, so a turned pad needs no special
 * case, and it matches {@link pointInPad} shape for shape.
 */
function segmentInPad(a: Vertex, b: Vertex, pf: FlatEntity): boolean {
  const pad = pf.entity as PadEntity;
  const c = pf.points[0];
  if (!c) return false;

  const hx = pad.sizeX / 2;
  const hy = pad.sizeY / 2;
  if (hx <= 0 || hy <= 0) return false;

  const rot = -(pad.rotation ?? 0) - (pf.owner?.rotation ?? 0);
  const p = rotatePoint(a.x - c.x, a.y - c.y, rot);
  const q = rotatePoint(b.x - c.x, b.y - c.y, rot);

  if (pad.type.charAt(1) === '2') {
    // Rectangular: the slab test, which is the standard segment-versus-box.
    let t0 = 0;
    let t1 = 1;
    for (const [from, to, lo, hi] of [
      [p.x, q.x, -hx - EPS, hx + EPS],
      [p.y, q.y, -hy - EPS, hy + EPS],
    ] as const) {
      const d = to - from;
      if (Math.abs(d) < 1e-12) {
        if (from < lo || from > hi) return false;   // parallel and outside
        continue;
      }
      const ta = (lo - from) / d;
      const tb = (hi - from) / d;
      t0 = Math.max(t0, Math.min(ta, tb));
      t1 = Math.min(t1, Math.max(ta, tb));
      if (t0 > t1) return false;
    }
    return true;
  }

  // Round or oval: scale the ellipse to a unit circle and take the closest
  // approach of the segment to the origin.
  const ax = p.x / hx;
  const ay = p.y / hy;
  const bx = q.x / hx;
  const by = q.y / hy;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 < 1e-18 ? 0
    : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2));
  const nx = ax + t * dx;
  const ny = ay + t * dy;
  return nx * nx + ny * ny <= 1 + EPS;
}

// ---------------------------------------------------------------------------
// Ratsnest
// ---------------------------------------------------------------------------

export interface RatLine {
  net: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Build the ratsnest for a board: for each net, the minimum spanning tree over
 * its pins, minus the connections already realised in copper.
 *
 * The original draws these on the dedicated Ratnest layer, which is why the
 * stock PCB layer stack has one.
 */
export function buildRatsnest(doc: CadDocument): RatLine[] {
  const { nets } = extractNetlist(doc);
  const out: RatLine[] = [];

  for (const net of nets) {
    // Nodes are islands of copper, not individual pins. Two pads already
    // joined by a trace are one island and need no ratline between them, so
    // routing a connection makes its ratline disappear without anything having
    // to delete it.
    const islands = net.groups
      .map((g) => dedupePins(g))
      .filter((g) => g.length > 0);
    if (islands.length < 2) continue;

    // Prim's algorithm over the islands. Nets are small, so the dense form is
    // fine, and the shortest link between two islands is the natural ratline.
    const inTree = [0];
    const rest = islands.map((_, i) => i).slice(1);

    while (rest.length > 0) {
      let best = { d: Infinity, a: null as PinRef | null, b: null as PinRef | null, bi: -1, island: -1 };

      for (const a of inTree) {
        for (let k = 0; k < rest.length; k++) {
          const b = rest[k]!;
          for (const pa of islands[a]!) {
            for (const pb of islands[b]!) {
              const d = (pa.x - pb.x) ** 2 + (pa.y - pb.y) ** 2;
              if (d < best.d) best = { d, a: pa, b: pb, bi: k, island: b };
            }
          }
        }
      }

      if (best.a === null || best.b === null) break;
      out.push({
        net: net.name,
        x1: best.a.x, y1: best.a.y,
        x2: best.b.x, y2: best.b.y,
      });
      inTree.push(best.island);
      rest.splice(best.bi, 1);
    }
  }

  return out;
}

/**
 * How much of each net is routed.
 *
 * `open` is the number of connections still to make — the ratline count — so
 * a board is finished when every net reports zero.
 */
export interface NetProgress {
  net: string;
  islands: number;
  open: number;
  pins: number;
}

export function routingProgress(doc: CadDocument): {
  nets: NetProgress[];
  open: number;
  routed: number;
} {
  const { nets } = extractNetlist(doc);
  const rows: NetProgress[] = [];
  let open = 0;
  let routed = 0;

  for (const net of nets) {
    const islands = net.groups.filter((g) => g.length > 0).length;
    if (net.pins.length < 2) continue;
    const remaining = Math.max(0, islands - 1);
    rows.push({ net: net.name, islands, open: remaining, pins: net.pins.length });
    open += remaining;
    if (remaining === 0) routed++;
  }

  return { nets: rows, open, routed };
}

// ---------------------------------------------------------------------------
// Connectivity report (Net Scan)
// ---------------------------------------------------------------------------

/** A pad identified by its designator and pin, in document space. */
export interface ReportNode { ref: string; pin: string; x: number; y: number; }

export interface ShortReport {
  /** The conflicting real net names, e.g. ["GND", "RX"]. */
  nets: string[];
  /** The pins caught up in the short. */
  nodes: ReportNode[];
  /** A point to jump to. */
  at: { x: number; y: number };
  entityIds: number[];
}

export interface UnroutedReport {
  net: string;
  from: ReportNode | null;
  to: ReportNode | null;
  x1: number; y1: number; x2: number; y2: number;
}

export interface ConnReport {
  shorts: ShortReport[];
  unrouted: UnroutedReport[];
  /** Nets fully routed (one island). */
  routed: number;
  /** Nets with two or more pins. */
  total: number;
}

/**
 * A board's connectivity at a glance: what is shorted (two real nets joined by
 * copper, with the pins involved) and what is still unrouted (the ratlines and
 * the pins they span). This is the data behind Net Scan — the answer to "what
 * is wrong and where".
 */
export function connectivityReport(doc: CadDocument): ConnReport {
  const nl = extractNetlist(doc);

  // Every pin's designator and world position, for naming nodes.
  const nodeById = new Map<number, ReportNode>();
  const walk = (list: Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { walk(e.children, e); continue; }
      if (e.kind === 'pad' && owner && (e as PadEntity).pin) {
        const w = placeInComponent({ x: (e as PadEntity).x, y: (e as PadEntity).y }, owner);
        nodeById.set(e.id, { ref: refDesOf(owner), pin: (e as PadEntity).pin!, x: w.x, y: w.y });
      }
    }
  };
  walk(doc.entities, null);

  const shorts: ShortReport[] = [];
  for (const c of nl.conflicts) {
    if (c.kind !== 'signal-mismatch') continue;
    const nodes = c.entityIds
      .map((id) => nodeById.get(id))
      .filter((n): n is ReportNode => n !== undefined);
    const nets = c.message.split(': ').pop()?.split(', ') ?? [];
    const at = nodes[0] ?? { ref: '', pin: '', x: 0, y: 0 };
    shorts.push({ nets, nodes, at: { x: at.x, y: at.y }, entityIds: c.entityIds });
  }

  const unrouted: UnroutedReport[] = [];
  for (const r of buildRatsnest(doc)) {
    const pins = nl.nets.find((n) => n.name === r.net)?.pins ?? [];
    const nearest = (x: number, y: number): ReportNode | null => {
      let best: PinRef | null = null;
      let bd = Infinity;
      for (const p of pins) {
        const d = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (d < bd) { bd = d; best = p; }
      }
      return best ? { ref: best.ref, pin: best.pin, x: best.x, y: best.y } : null;
    };
    unrouted.push({
      net: r.net, from: nearest(r.x1, r.y1), to: nearest(r.x2, r.y2),
      x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2,
    });
  }

  const scored = nl.nets.filter((n) => n.pins.length > 1);
  return {
    shorts,
    unrouted,
    total: scored.length,
    routed: scored.filter((n) => n.groups.filter((g) => g.length > 0).length <= 1).length,
  };
}

/** Collapse pins that sit at the same point. */
function dedupePins(pins: PinRef[]): PinRef[] {
  const out: PinRef[] = [];
  for (const p of pins) {
    if (out.some((q) => Math.abs(q.x - p.x) <= EPS && Math.abs(q.y - p.y) <= EPS)) continue;
    out.push(p);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------

/** Render a netlist in the tab-separated form the original's BOM/net output uses. */
export function formatNetlist(nl: Netlist): string {
  const lines: string[] = [];
  for (const net of nl.nets) {
    if (!net.pins.length) continue;
    lines.push(net.name);
    for (const p of net.pins) lines.push(`\t${p.ref}\t${p.pin}\t${p.component}`);
  }
  return lines.join('\r\n');
}

// ---------------------------------------------------------------------------
// Remembering the last netlist
// ---------------------------------------------------------------------------

/*
 * Extraction walks every piece of copper on the board and is the most
 * expensive thing here — better than half a minute on the largest designs — so
 * anything that merely wants to *ask* a question of the netlist ("which net is
 * this pad on?") cannot afford to rebuild it each time.
 *
 * One remembered result is enough. Work happens on one board at a time, and
 * moving to another simply re-extracts. The caller must call
 * `invalidateNetlist` whenever a document changes, or this will answer from a
 * board that no longer exists.
 */
let lastDoc: CadDocument | null = null;
let lastNetlist: Netlist | null = null;

/** The netlist for `doc`, reusing the last result when nothing has changed. */
export function cachedNetlist(doc: CadDocument): Netlist {
  if (lastDoc === doc && lastNetlist) return lastNetlist;
  lastNetlist = extractNetlist(doc);
  lastDoc = doc;
  return lastNetlist;
}

/** Forget the remembered netlist. Call on every edit. */
export function invalidateNetlist(): void {
  lastDoc = null;
  lastNetlist = null;
}

/**
 * What the netlist calls the copper this entity belongs to.
 *
 * This is not the same as the entity's own `Signal:`. A pad may carry no
 * signal of its own and still be part of GND because a trace joins it there;
 * that is precisely what a netlist is for, and what the editor should show.
 *
 * Pads inside a component are found by (refdes, pin) as well as by id, since a
 * placed part's pins are named by the netlist rather than by the pad.
 */
export function netOfEntity(doc: CadDocument, id: number): string | null {
  const nl = cachedNetlist(doc);
  for (const net of nl.nets) {
    if (net.entityIds.includes(id)) return net.name;
  }
  return null;
}
